/* eslint-disable no-console */

import chalk from 'chalk';
import * as service from './service';
import { Router } from 'express';
import { getLogger } from '../../../commons/logger';
import { NpmPackageError, TimeoutError } from '../../../errors';
import { getInvalidPackageNames, getInvalidPackagePaths } from '../../../utils/npmUtils';
import type { AwaitedReturnType } from 'src/utils';
import type { IncomingParams } from 'clickim/src/background/playbackContext';

const logger = getLogger('cli-router');

export const router = Router();

type RunStepResBody = { success: false; code: string; message?: string } | { success: true; data?: AwaitedReturnType<typeof service['runCodeWithPackages']> };
interface RunStepReqBody {
    code?: string;
    stepId?: string;
    testResultId?: string;
    stepResultId?: string;
    retryIndex?: number;
    timeout?: number;
    fileDataUrl?: string;
    incomingParams: Pick<IncomingParams, 'as' | 'nodePackageParams'>;
    context: { [key: string]: unknown };
}
router.post<never, RunStepResBody, RunStepReqBody>('/run', async (req, res) => {
    const { code, stepId, incomingParams, context, testResultId, retryIndex, stepResultId, timeout, fileDataUrl } = req.body;

    if (typeof code !== 'string' || !stepId || !incomingParams || !context || !testResultId || typeof retryIndex !== 'number' || !stepResultId || typeof timeout !== 'number') {
        res.status(400).json({ success: false, code: 'invalid-params' });
        return;
    }

    // Validate package paths to prevent path traversal
    if (incomingParams.nodePackageParams) {
        const packagePaths = incomingParams.nodePackageParams
            .map(param => param.testimPackageLocalLocation)
            .filter(Boolean)
        const invalidPaths = getInvalidPackagePaths(packagePaths);
        if (invalidPaths) {
            logger.error('Invalid package paths detected', { invalidPaths, stepId, testResultId });
            res.status(400).json({ success: false, code: 'invalid-package-path', message: `Invalid package path(s) detected: ${invalidPaths.join(', ')}` });
            return;
        }
    }

    const commonLogData = { stepId, testResultId, retryIndex, stepResultId, timeout, fileDataUrl, nodePackageParams: incomingParams.nodePackageParams };
    try {
        const data = await service.runCodeWithPackages(code, stepId, incomingParams, context, testResultId, retryIndex, stepResultId, timeout, fileDataUrl);
        if (!data.success) {
            console.log(chalk.red(data.result.resultValue));
            logger.error('CLI Action Failure', { message: data.result.resultValue, ...commonLogData });
        }
        res.status(200).json({ success: true, data });
    } catch (err) {
        logger.error('failed to run cli code', { err, ...commonLogData });
        console.log(chalk.red('failed to run cli code', err));
        res.status(500).json({ success: false, code: 'internal-error' });
    }
});

type InstallPkgsResBody = { success: false; code: string; message?: string } | { success: true; data?: AwaitedReturnType<typeof service['installPackage']> };
interface InstallPkgsReqBody { stepId?: string; testResultId?: string; stepResultId?: string; retryIndex?: number; timeout?: number; packageData?: { packageName: string; packageVersion: string }[] }
router.post<never, InstallPkgsResBody, InstallPkgsReqBody>('/install', async (req, res) => {
    const { stepId, testResultId, retryIndex, packageData, stepResultId, timeout } = req.body;

    if (!stepId || typeof packageData !== 'object' || !testResultId || typeof retryIndex !== 'number' || !stepResultId || typeof timeout !== 'number') {
        res.status(400).json({ success: false, code: 'invalid-params' });
        return;
    }

    // Validate package names
    const invalidNames = getInvalidPackageNames(packageData);
    if (invalidNames) {
        logger.error('Invalid package names detected', { invalidNames, stepId, testResultId });
        res.status(400).json({ success: false, code: 'invalid-package-name', message: `Invalid package name(s): ${invalidNames.join(', ')}` });
        return;
    }

    const commonLogData = { stepId, testResultId, retryIndex, stepResultId, timeout, packageData };
    try {
        const data = await service.installPackage(stepId, testResultId, retryIndex, packageData, stepResultId, timeout);
        logger.info('installed packages successfully', commonLogData);
        res.status(200).json({ success: true, data });
    } catch (err) {
        if (err instanceof NpmPackageError) {
            logger.error('failed to install node packages', { err, ...commonLogData });
            res.status(200).json({ success: false, code: 'invalid-node-package', message: err.message });
            return;
        }
        if (err instanceof TimeoutError) {
            logger.error('timeout installing node package', { err, ...commonLogData });
            res.status(200).json({ success: false, code: 'timeout' });
            return;
        }
        logger.error('failed to install node packages', { err, ...commonLogData });
        res.status(500).json({ success: false, code: 'internal-error' });
    }
});
